if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.formInput = this.form?.querySelector("[name=id]");
        if (this.formInput) this.formInput.disabled = false;
        this.form?.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart = document.querySelector("cart-drawer");
        this.cartType = this.cart?.dataset.cartType || "page";

        this.submitButton = this.querySelector('[type="submit"]');
        this.body = document.querySelector("body");

        if (document.querySelector("cart-drawer"))
          this.submitButton?.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";

        this.handleErrorMessage = this.handleErrorMessage.bind(this);
        this.redirectAfterSubmit = this.redirectAfterSubmit.bind(this);
        this.disableLoadingInButton = this.disableLoadingInButton.bind(this);
      }

      normalizeToken(value) {
        return String(value || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      }

      hasAnyToken(haystack, tokens) {
        return tokens.some((token) => haystack.includes(token));
      }

      getProductScope() {
        return this.closest("section.wt-product__wrapper") || document;
      }

      resolveExplicitPurchaseMode() {
        const productScope = this.getProductScope();
        const normalizeMode = (value) => {
          const normalizedValue = String(value || "")
            .trim()
            .toLowerCase();
          if (normalizedValue === "otp") return "otp";
          if (normalizedValue === "sub") return "sub";
          return "";
        };

        const bundleModeInput = productScope.querySelector(
          ".lsg-bundle-interval-select input[name^='bundle_interval_select_']:checked",
        );
        const bundleMode = normalizeMode(bundleModeInput?.value);
        if (bundleMode) return bundleMode;

        const pdpModeInput = productScope.querySelector(
          "[data-pdp-interval-select] input[name^='pdp_interval_select_']:checked",
        );
        const pdpMode = normalizeMode(pdpModeInput?.value);
        if (pdpMode) return pdpMode;

        const bundleRoot = productScope.querySelector(
          "stay-ai-bundle-product, .lsg-bundle-block, .lsg-bundle-summary-block",
        );
        if (bundleRoot?.classList?.contains("lsg-bundle--otp-selected")) {
          return "otp";
        }
        if (bundleRoot?.classList?.contains("lsg-bundle--sub-selected")) {
          return "sub";
        }

        const otpTokens = [
          "one time",
          "one time purchase",
          "onetime",
          "otp",
          "single purchase",
          "buy once",
        ];
        const subTokens = [
          "subscribe",
          "subscription",
          "autoship",
          "auto ship",
          "recurring",
          "delivery every",
        ];

        const explicitModeCandidates = Array.from(
          productScope.querySelectorAll(
            "input[type='radio']:checked, input[type='checkbox']:checked, [aria-pressed='true'], [aria-selected='true'], .is-active, .is-selected",
          ),
        );

        let otpScore = 0;
        let subScore = 0;
        explicitModeCandidates.forEach((control) => {
          const descriptor = this.getControlDescriptor(control);
          if (!descriptor) return;
          const hasOtp = this.hasAnyToken(descriptor, otpTokens);
          const hasSub = this.hasAnyToken(descriptor, subTokens);
          if (hasOtp && !hasSub) otpScore += 1;
          if (hasSub && !hasOtp) subScore += 1;
        });

        if (otpScore > 0 && subScore === 0) return "otp";
        if (subScore > 0 && otpScore === 0) return "sub";
        return null;
      }

      getControlDescriptor(control) {
        if (!control) return "";

        const id = control.getAttribute("id") || "";
        let labelText = "";
        if (id) {
          const escapedId =
            typeof CSS !== "undefined" && typeof CSS.escape === "function"
              ? CSS.escape(id)
              : id;
          const label = document.querySelector(`label[for="${escapedId}"]`);
          labelText = label?.textContent || "";
        } else {
          labelText = control.closest("label")?.textContent || "";
        }

        return this.normalizeToken(
          [
            control.getAttribute("name"),
            control.getAttribute("id"),
            control.getAttribute("value"),
            control.getAttribute("class"),
            control.textContent,
            labelText,
          ].join(" "),
        );
      }

      detectPurchaseMode() {
        const explicitMode = this.resolveExplicitPurchaseMode();
        if (explicitMode) return explicitMode;

        const productScope = this.getProductScope();
        const candidates = Array.from(
          productScope.querySelectorAll(
            "input[type='radio']:checked, input[type='checkbox']:checked, select, option:checked",
          ),
        ).filter((control) => {
          const descriptor = this.getControlDescriptor(control);
          return /plan|purchase|subscription|selling/.test(descriptor);
        });

        if (!candidates.length) return null;

        const otpTokens = [
          "one time",
          "one time purchase",
          "onetime",
          "otp",
          "single purchase",
          "buy once",
        ];
        const subTokens = [
          "subscribe",
          "subscription",
          "autoship",
          "auto ship",
          "recurring",
          "delivery every",
        ];

        let otpScore = 0;
        let subScore = 0;
        let explicitOtp = false;
        let explicitSub = false;

        candidates.forEach((control) => {
          const descriptor = this.getControlDescriptor(control);
          const hasOtp = this.hasAnyToken(descriptor, otpTokens);
          const hasSub = this.hasAnyToken(descriptor, subTokens);

          if (hasOtp && !hasSub) {
            otpScore += 1;
            explicitOtp = true;
          }
          if (hasSub && !hasOtp) {
            subScore += 1;
            explicitSub = true;
          }

          const name = this.normalizeToken(control.getAttribute("name"));
          const value = String(control.getAttribute("value") || "").trim();
          if (name.includes("selling plan")) {
            if (value) subScore += 1;
            else otpScore += 1;
          }
        });

        const activeToggles = Array.from(
          productScope.querySelectorAll(
            "[aria-pressed='true'], [aria-selected='true'], .is-active, .is-selected, .active, .selected",
          ),
        );

        activeToggles.forEach((toggle) => {
          const descriptor = this.normalizeToken(toggle.textContent || "");
          if (!descriptor) return;
          const hasOtp = this.hasAnyToken(descriptor, otpTokens);
          const hasSub = this.hasAnyToken(descriptor, subTokens);
          if (hasOtp && !hasSub) {
            otpScore += 1;
            explicitOtp = true;
          }
          if (hasSub && !hasOtp) {
            subScore += 1;
            explicitSub = true;
          }
        });

        if (explicitOtp && !explicitSub) return "otp";
        if (explicitSub && !explicitOtp) return "sub";
        if (otpScore > subScore) return "otp";
        if (subScore > otpScore) return "sub";
        return null;
      }

      resolveSelectedSellingPlanValue() {
        const productScope = this.getProductScope();
        const candidates = [
          productScope.querySelector(
            "[data-pdp-interval-select] select[name='selling_plan']",
          ),
          productScope.querySelector("select[name='stayai_selling_plan']"),
          productScope.querySelector("select.stayai-subscription-dropdown"),
          productScope.querySelector("select[name='selling_plan']"),
          productScope.querySelector("input[name*='selling_plan']:checked"),
          productScope.querySelector("input[name='selling_plan']"),
        ];

        for (const candidate of candidates) {
          if (!candidate || candidate.disabled) continue;
          const value = String(candidate?.value || "").trim();
          if (value && value !== "0") return value;
        }

        return "";
      }

      getProductHandleForSync() {
        const formAction = String(this.form?.getAttribute("action") || "");
        const actionMatch = formAction.match(/\/products\/([^/?]+)/i);
        if (actionMatch?.[1]) {
          return decodeURIComponent(actionMatch[1]);
        }
        const pathMatch = String(window.location.pathname || "").match(
          /\/products\/([^/]+)/i,
        );
        if (pathMatch?.[1]) {
          return decodeURIComponent(pathMatch[1]);
        }
        return "";
      }

      normalizeSellingPlanFormData(formData) {
        if (!formData) return;

        const explicitMode = this.resolveExplicitPurchaseMode();
        if (explicitMode === "otp") {
          formData.delete("selling_plan");
          return;
        }
        if (explicitMode === "sub") {
          const selectedSellingPlan = this.resolveSelectedSellingPlanValue();
          if (selectedSellingPlan) {
            formData.set("selling_plan", selectedSellingPlan);
          }
          return;
        }
        formData.delete("selling_plan");
      }

      closeComplementaryProduct() {
        const complementaryOverlayPage = document.querySelector(
          ".wt__quick-buy--page-overlay",
        );
        if (this.body.classList.contains("quick-buy-page-overlay"))
          this.body.classList.remove("quick-buy-page-overlay");
        if (
          complementaryOverlayPage.classList.contains(
            "wt__quick-buy--page-overlay--open",
          )
        )
          complementaryOverlayPage.classList.remove(
            "wt__quick-buy--page-overlay--open",
          );
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();
        this.cart?.setActiveElement(document.activeElement);

        const loader = this.querySelector(".loading-overlay__spinner");

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        if (loader) loader.classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        const explicitPurchaseMode = this.resolveExplicitPurchaseMode();
        this.normalizeSellingPlanFormData(formData);
        const selectedSellingPlanId = Number(formData.get("selling_plan") || 0);
        const purchaseModeForSync =
          explicitPurchaseMode === "sub"
            ? "sub"
            : "otp";
        const productHandleForSync = this.getProductHandleForSync();
        if (this.cart && typeof this.cart.getSectionsToRender === "function") {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
        } else {
          formData.append("sections", []);
        }

        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.error = true;

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");

              this.dispatchEvent(
                new CustomEvent("cart-drawer:refresh", {
                  detail: { response: response },
                  bubbles: true,
                  composed: true,
                }),
              );

              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              return;
            }

            let syncResult = { changed: false, noop: true };
            if (
              window.AvCartSync &&
              typeof window.AvCartSync.syncAfterAdd === "function"
            ) {
              try {
                const syncPayload = {
                  source: "product-form",
                  addedVariantId: Number(formData.get("id") || 0),
                  addedSellingPlanId:
                    purchaseModeForSync === "sub" ? selectedSellingPlanId : 0,
                  purchaseMode: purchaseModeForSync,
                  addedProductHandle: productHandleForSync,
                };
                syncResult = await window.AvCartSync.syncAfterAdd(syncPayload);
              } catch (syncError) {
                console.error(syncError);
              }
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.redirectAfterSubmit(response, true, syncResult);
                  });
                },
                { once: true },
              );
              if (this.cartType === "drawer") quickAddModal.hide(true);
            } else {
              const isClosedCart = !document.body.classList.contains(
                "page-overlay-cart-on",
              );
              this.redirectAfterSubmit(response, isClosedCart, syncResult);
            }
          })
          .catch((e) => {
            console.error(e);
            if (e instanceof TypeError && e.message.includes("'cart-drawer'")) {
              location.reload();
            }
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            this.submitButton.removeAttribute("aria-disabled");
            if (this.error) {
              this.disableLoadingInButton();
              return;
            }
            this.quick_add_container = document.querySelector(
              ".wt__quick-buy__container",
            );
            this.quick_add_overlay = document.querySelector(
              ".wt__quick-buy--page-overlay",
            );
            this.quick_add_product =
              this.quick_add_container.querySelector(".wt-product");
            this.loader = this.quick_add_container.querySelector(
              ".wt__quick-buy-loader",
            );

            if (!this.quick_add_container.classList.contains("hidden")) {
              // use handle and quickbuy closeCart()

              this.quick_add_product?.remove();
              this.quick_add_container.classList.remove(
                "wt__quick-buy__container--open",
              );
              this.quick_add_overlay.classList.remove(
                "wt__quick-buy--page-overlay--open",
              );
              document.body.classList.remove("quick-buy-page-overlay");
              this.loader.classList.remove("hidden");
            }

            // delay button loading
            setTimeout(() => {
              this.disableLoadingInButton();
            }, 200);
          });
      }

      disableLoadingInButton() {
        this.submitButton.classList.remove("loading");
        this.querySelector(".loading-overlay__spinner").classList.add("hidden");
      }

      redirectAfterSubmit(response, isClosedCart = true, syncResult = null) {
        const body = document.body;
        const isCartPage = body.classList.contains("template-cart");

        if (this.cartType === "drawer" && isCartPage) {
          const overlay = document.querySelector(
            ".wt__quick-buy--page-overlay",
          );
          overlay.classList.remove("wt__quick-buy--page-overlay--open");
          body.classList.remove("quick-buy-page-overlay");
        } else if (this.cartType === "drawer") {
          this.cart.renderContents(response, isClosedCart);
          if (
            syncResult?.changed &&
            this.cart &&
            typeof this.cart.refreshCartDrawer === "function"
          ) {
            this.cart.refreshCartDrawer().catch((error) => {
              console.error(error);
            });
          }
          return;
        }
        window.location = window.routes.cart_url;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
